import { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, Play, X, Loader2, CalendarClock } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import { AdminLayout } from './AdminLayout';
import {
  CreateClientInput,
  RevenueAutomationClient,
  UpdateClientInput,
  backprocessRevenueAutomationClient,
  createRevenueAutomationClient,
  deleteRevenueAutomationClient,
  listRevenueAutomationClients,
  runRevenueAutomationClient,
  updateRevenueAutomationClient,
} from '../../../services/admin/revenueAutomationClientsService';
import {
  RevenueSource,
  getAvailableRevenueSources,
  getRevenueSourceDisplayName,
} from '../../../types/RevenueSource';

interface FormDraft {
  name: string;
  revenueSource: RevenueSource;
  loginEmail: string;
  loginPassword: string;
  isEnabled: boolean;
  // Per-source config fields, flattened into the draft for easy editing.
  storeIds: string;
}

const emptyDraft = (): FormDraft => ({
  name: '',
  revenueSource: RevenueSource.BOOQ,
  loginEmail: '',
  loginPassword: '',
  isEnabled: true,
  storeIds: '',
});

const configFromDraft = (draft: FormDraft): Record<string, unknown> => {
  const config: Record<string, unknown> = {};
  if (draft.revenueSource === RevenueSource.BOOQ && draft.storeIds.trim()) {
    config.storeIds = draft.storeIds.trim();
  }
  return config;
};

/** Sources whose platform implements backprocessing (mirrors the server's PLATFORMS map). */
const BACKPROCESS_SOURCES = new Set<RevenueSource>([RevenueSource.CASHDESK, RevenueSource.BOOQ, RevenueSource.SCANFIE, RevenueSource.UNTIL]);

/** Yesterday in YYYY-MM-DD — the latest date a backprocess can target. */
const yesterdayIso = ((): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
})();

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

export default function AdminRevenueAutomationPage() {
  const [clients, setClients] = useState<RevenueAutomationClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [draft, setDraft] = useState<FormDraft>(emptyDraft());
  const [saving, setSaving] = useState(false);

  const [runningId, setRunningId] = useState<string | null>(null);
  const [runMessage, setRunMessage] = useState<{ id: string; success: boolean; text: string } | null>(null);

  const [backprocessClient, setBackprocessClient] = useState<RevenueAutomationClient | null>(null);
  const [backprocessStart, setBackprocessStart] = useState('');
  const [backprocessEnd, setBackprocessEnd] = useState('');
  const [backprocessing, setBackprocessing] = useState(false);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listRevenueAutomationClients();
      setClients(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const openAdd = () => {
    setDraft(emptyDraft());
    setShowAddModal(true);
  };

  const openEdit = (c: RevenueAutomationClient) => {
    const storeIdsRaw = c.config?.storeIds;
    setDraft({
      name: c.name,
      revenueSource: c.revenueSource,
      loginEmail: c.loginEmail,
      loginPassword: '',
      isEnabled: c.isEnabled,
      storeIds: storeIdsRaw == null ? '' : String(storeIdsRaw),
    });
    setEditingId(c.id);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const submitAdd = async () => {
    if (!draft.name.trim() || !draft.loginEmail.trim() || !draft.loginPassword) {
      alert('Name, login email and login password are required.');
      return;
    }
    setSaving(true);
    try {
      const input: CreateClientInput = {
        name: draft.name.trim(),
        revenueSource: draft.revenueSource,
        loginEmail: draft.loginEmail.trim(),
        loginPassword: draft.loginPassword,
        isEnabled: draft.isEnabled,
        config: configFromDraft(draft),
      };
      await createRevenueAutomationClient(input);
      closeModals();
      await reload();
    } catch (e) {
      alert(`Failed to create: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!editingId) return;
    if (!draft.name.trim() || !draft.loginEmail.trim()) {
      alert('Name and login email are required.');
      return;
    }
    setSaving(true);
    try {
      const input: UpdateClientInput = {
        name: draft.name.trim(),
        revenueSource: draft.revenueSource,
        loginEmail: draft.loginEmail.trim(),
        isEnabled: draft.isEnabled,
        config: configFromDraft(draft),
      };
      if (draft.loginPassword) input.loginPassword = draft.loginPassword;
      await updateRevenueAutomationClient(editingId, input);
      closeModals();
      await reload();
    } catch (e) {
      alert(`Failed to update: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (c: RevenueAutomationClient) => {
    if (!window.confirm(`Delete automation client "${c.name}"?`)) return;
    try {
      await deleteRevenueAutomationClient(c.id);
      await reload();
    } catch (e) {
      alert(`Failed to delete: ${(e as Error).message}`);
    }
  };

  const openBackprocess = (c: RevenueAutomationClient) => {
    setBackprocessClient(c);
    setBackprocessStart('');
    setBackprocessEnd(yesterdayIso);
  };

  const closeBackprocess = () => {
    setBackprocessClient(null);
    setBackprocessStart('');
    setBackprocessEnd('');
  };

  const submitBackprocess = async () => {
    if (!backprocessClient) return;
    if (!backprocessStart || !backprocessEnd) {
      alert('Start and end date are required.');
      return;
    }
    if (backprocessStart > backprocessEnd) {
      alert('Start date must be on or before end date.');
      return;
    }
    setBackprocessing(true);
    try {
      const result = await backprocessRevenueAutomationClient(
        backprocessClient.id,
        backprocessStart,
        backprocessEnd,
      );
      setRunMessage({
        id: backprocessClient.id,
        success: true,
        text: `Backprocessing started for ${result.dayCount} day(s) (${result.startDate} → ${result.endDate}). One email will arrive per day, oldest first.`,
      });
      closeBackprocess();
    } catch (e) {
      alert(`Failed to start backprocessing: ${(e as Error).message}`);
    } finally {
      setBackprocessing(false);
    }
  };

  const onRun = async (c: RevenueAutomationClient) => {
    setRunningId(c.id);
    setRunMessage(null);
    try {
      const result = await runRevenueAutomationClient(c.id);
      setRunMessage({
        id: c.id,
        success: result.success,
        text: result.success ? 'Run succeeded.' : `Run failed: ${result.error ?? 'unknown error'}`,
      });
      await reload();
    } catch (e) {
      setRunMessage({ id: c.id, success: false, text: `Run failed: ${(e as Error).message}` });
    } finally {
      setRunningId(null);
    }
  };

  return (
    <AdminLayout>
      <div className={cn('flex items-center justify-between mb-6')}>
        <div>
          <h2 className={cn(primaryText, 'text-2xl', semiBoldText)}>Revenue Automation</h2>
          <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
            POS logins for the headless-browser daily revenue download.
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white text-sm font-medium hover:bg-purple-700 dark:hover:bg-purple-700 transition"
        >
          <Plus className="w-4 h-4" /> Add client
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {runMessage && (
        <div
          className={cn(
            'mb-4 p-3 rounded-lg text-sm border',
            runMessage.success
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
          )}
        >
          {runMessage.text}
        </div>
      )}

      <div className={cn(cardClass, 'overflow-hidden p-0')}>
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-gray-400 dark:text-textQuaternary-dark" />
          </div>
        ) : clients.length === 0 ? (
          <div className={cn('p-8 text-center', secondaryText, smallTextSize)}>
            No automation clients yet. Click "Add client" to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr className="text-left">
                  <th className={cn('px-4 py-3', smallTextSize, secondaryText, semiBoldText)}>Name</th>
                  <th className={cn('px-4 py-3', smallTextSize, secondaryText, semiBoldText)}>Source</th>
                  <th className={cn('px-4 py-3', smallTextSize, secondaryText, semiBoldText)}>Login email</th>
                  <th className={cn('px-4 py-3', smallTextSize, secondaryText, semiBoldText)}>Enabled</th>
                  <th className={cn('px-4 py-3', smallTextSize, secondaryText, semiBoldText)}>Last run</th>
                  <th className={cn('px-4 py-3', smallTextSize, secondaryText, semiBoldText)}>Status</th>
                  <th className={cn('px-4 py-3', smallTextSize, secondaryText, semiBoldText, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <td className={cn('px-4 py-3', primaryText, smallTextSize)}>{c.name}</td>
                    <td className={cn('px-4 py-3', secondaryText, smallTextSize)}>{getRevenueSourceDisplayName(c.revenueSource)}</td>
                    <td className={cn('px-4 py-3', secondaryText, smallTextSize)}>{c.loginEmail}</td>
                    <td className={cn('px-4 py-3', smallTextSize)}>
                      <span className={cn(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        c.isEnabled
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      )}>
                        {c.isEnabled ? 'On' : 'Off'}
                      </span>
                    </td>
                    <td className={cn('px-4 py-3', secondaryText, smallTextSize)}>{formatDateTime(c.lastRunAt)}</td>
                    <td className={cn('px-4 py-3', smallTextSize)}>
                      <StatusBadge client={c} />
                    </td>
                    <td className={cn('px-4 py-3', smallTextSize, 'text-right')}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => onRun(c)}
                          disabled={runningId === c.id}
                          title="Run now"
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
                        >
                          {runningId === c.id ? <Loader2 className="w-4 h-4 animate-spin text-purple-600 dark:text-purple-400" /> : <Play className="w-4 h-4 text-purple-600 dark:text-purple-400" />}
                        </button>
                        {BACKPROCESS_SOURCES.has(c.revenueSource) && (
                          <button
                            onClick={() => openBackprocess(c)}
                            title="Backprocess date range"
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                          >
                            <CalendarClock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(c)}
                          title="Edit"
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                        >
                          <Edit2 className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                        </button>
                        <button
                          onClick={() => onDelete(c)}
                          title="Delete"
                          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 transition"
                        >
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showAddModal || editingId) && (
        <Modal
          title={editingId ? 'Edit revenue automation client' : 'Add revenue automation client'}
          onClose={closeModals}
        >
          <FormFields
            draft={draft}
            setDraft={setDraft}
            isEdit={Boolean(editingId)}
          />
          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={closeModals}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={editingId ? submitEdit : submitAdd}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white text-sm font-medium hover:bg-purple-700 dark:hover:bg-purple-700 transition disabled:opacity-50"
            >
              {saving ? 'Saving…' : editingId ? 'Save' : 'Create'}
            </button>
          </div>
        </Modal>
      )}

      {backprocessClient && (
        <Modal
          title={`Backprocess — ${backprocessClient.name}`}
          onClose={closeBackprocess}
        >
          <div className="space-y-4">
            <p className={cn(secondaryText, smallTextSize)}>
              Downloads every day in the range and sends one email per day, oldest
              first. The end date is capped at yesterday — today and future dates
              are never processed. The run continues in the background; watch the
              inbox for progress.
            </p>
            <Field label="Start date">
              <input
                type="date"
                value={backprocessStart}
                max={yesterdayIso}
                onChange={e => setBackprocessStart(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="End date (capped at yesterday)">
              <input
                type="date"
                value={backprocessEnd}
                max={yesterdayIso}
                onChange={e => setBackprocessEnd(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={closeBackprocess}
              disabled={backprocessing}
              className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submitBackprocess}
              disabled={backprocessing}
              className="px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white text-sm font-medium hover:bg-purple-700 dark:hover:bg-purple-700 transition disabled:opacity-50"
            >
              {backprocessing ? 'Starting…' : 'Start backprocessing'}
            </button>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

const StatusBadge = ({ client }: { client: RevenueAutomationClient }) => {
  if (!client.lastRunAt || !client.lastRunStatus) {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">Never run</span>;
  }
  if (client.lastRunStatus === 'success') {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">Success</span>;
  }
  return (
    <span
      title={client.lastRunError ?? undefined}
      className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 cursor-help"
    >
      Failed
    </span>
  );
};

const Modal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
    <div
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 className={cn(primaryText, 'text-lg', semiBoldText)}>{title}</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
          <X className="w-5 h-5 text-gray-500 dark:text-textTertiary-dark" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const FormFields = ({
  draft,
  setDraft,
  isEdit,
}: {
  draft: FormDraft;
  setDraft: (d: FormDraft) => void;
  isEdit: boolean;
}) => (
  <div className="space-y-4">
    <Field label="Name (used in email subject)">
      <input
        type="text"
        value={draft.name}
        onChange={e => setDraft({ ...draft, name: e.target.value })}
        placeholder="Restaurant name"
        className={inputClass}
      />
    </Field>
    <Field label="Source">
      <select
        value={draft.revenueSource}
        onChange={e => setDraft({ ...draft, revenueSource: e.target.value as RevenueSource })}
        className={inputClass}
      >
        {getAvailableRevenueSources().filter(s => s !== RevenueSource.LIGHTSPEED).map(s => (
          <option key={s} value={s}>{getRevenueSourceDisplayName(s)}</option>
        ))}
      </select>
    </Field>
    <Field label="Login email">
      <input
        type="email"
        value={draft.loginEmail}
        onChange={e => setDraft({ ...draft, loginEmail: e.target.value })}
        className={inputClass}
      />
    </Field>
    <Field label={isEdit ? 'Login password (leave empty to keep existing)' : 'Login password'}>
      <input
        type="password"
        value={draft.loginPassword}
        onChange={e => setDraft({ ...draft, loginPassword: e.target.value })}
        placeholder={isEdit ? 'Leave empty to keep existing' : ''}
        className={inputClass}
      />
    </Field>
    {draft.revenueSource === RevenueSource.BOOQ && (
      <Field label="Store IDs (Booq)">
        <input
          type="text"
          value={draft.storeIds}
          onChange={e => setDraft({ ...draft, storeIds: e.target.value })}
          placeholder="e.g. 9296"
          className={inputClass}
        />
      </Field>
    )}
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={draft.isEnabled}
        onChange={e => setDraft({ ...draft, isEnabled: e.target.checked })}
        className="w-4 h-4"
      />
      <span className={cn(primaryText, smallTextSize)}>Enabled</span>
    </label>
  </div>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className={cn(secondaryText, smallTextSize, 'block mb-1')}>{label}</label>
    {children}
  </div>
);

const inputClass =
  'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500';
